/**
 * iter442e — Reusable "Pick from Bank" dialog.
 *
 * Lets the user attach an existing unlinked bank-statement transaction
 * to an Expense / Bill payment / Supplier payment form, instead of
 * re-typing amount + date + description manually.
 *
 * iter448 — When `onPickMulti` is provided, the dialog switches to
 * "group + multi-pick" mode: rows with similar names (same decoded
 * label or same description prefix) collapse into a group; clicking
 * the group expands it; user ticks rows + optionally picks a branch
 * per row; "Add N selected" returns an array of {txn, branch_id?} to
 * the parent. The parent batches them into one or more
 * /banking/bank-payments/bulk-from-stmt-txns calls (grouped by branch
 * server-side limit of 50/call).
 *
 * Single-pick mode (legacy `onPick(txn)`) is unchanged.
 */
import { cur } from '@/lib/currency';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Banknote, Search, RefreshCw, Loader2, Building2,
  ChevronDown, ChevronRight, Layers, Zap, Pin, X, Cloud, FileDown, FileUp,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { localToday } from '@/lib/dateRange';

/** Tokens to ignore when building a group key from a description.
 *  These are usually transaction IDs, dates, or noise. */
const _DIGIT_RE = /[0-9]/;
const _LONG_TOKEN_RE = /^[A-Z0-9_\-:]{8,}$/i;

function groupKeyFor(row, byMonth = false) {
  let key;
  if (row?.decoded_label) {
    key = row.decoded_label;
  } else {
    const desc = (row?.description || '').trim();
    if (!desc) {
      key = '(no description)';
    } else {
      // Build a normalised prefix: keep the first 4 alphabetic-ish tokens,
      // strip digits / long IDs / standalone dashes. Examples:
      //   "Master Card Transactions Fees - MASTER TRM 64238235-TXN:D0010-C0000"
      //   → "Master Card Transactions Fees - MASTER TRM"
      const tokens = desc.split(/\s+/);
      const kept = [];
      for (const t of tokens) {
        if (!t) continue;
        if (_LONG_TOKEN_RE.test(t)) break;        // hit a long ID → stop
        if (_DIGIT_RE.test(t) && t.length > 3) break;
        kept.push(t);
        if (kept.length >= 5) break;
      }
      key = (kept.join(' ') || desc.slice(0, 40)).replace(/\s+-\s*$/, '').trim();
    }
  }
  // iter462 — Optionally bucket by calendar month so a long run of
  // similarly-named fees (VAT-MASTER, VAT-SPAN, MasterCard Fees…)
  // collapses to "{label} · {YYYY-MM}" instead of one row per TRM/TXN.
  if (byMonth) {
    const iso = (row?.date || '').slice(0, 7); // YYYY-MM
    if (iso) key = `${key} · ${iso}`;
  }
  return key;
}

export default function PickFromBankDialog({
  open,
  onClose,
  onPick,                      // single-pick: receives one txn object
  onPickMulti,                 // iter448 — multi-pick: receives [{txn, branch_id}, ...]
  onQuickGroup,                 // iter449 — quick-categorise a whole group at once
  direction = 'outgoing',
  entityType = null,
  entityId = null,
  entityName = '',
  defaultSearch = '',
  amountHint = null,
  branches = [],               // iter448 — used by the per-row branch dropdown
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(defaultSearch);
  const [days, setDays] = useState(180);
  // iter462 — Bank-account filter. The unlinked endpoint already accepts
  // `bank_account_id`; this state surfaces it as a top-of-dialog dropdown
  // so users can flip between banks without leaving the picker. When
  // empty (default) all banks are shown.
  const [bankFilter, setBankFilter] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  // iter462 — Group by calendar month in addition to the description
  // prefix. Default ON in bulk mode so the "VAT - SPAN TRM ...D0036",
  // "VAT - SPAN TRM ...D0111" rows collapse to a single
  // "VAT - SPAN (May 2026 · 8 txns)" bucket.
  const [groupByMonth, setGroupByMonth] = useState(true);
  // iter461 — Default to ALL banks. Previously this defaulted to false,
  // which routed through /banking/statement-txn/match-payee — that path
  // only surfaces transactions on banks the entity has been linked to
  // before, so a fresh supplier (or any supplier paid from a 2nd bank
  // for the first time) appeared to have "no bank matches". Users
  // expected to see every unlinked line across every bank by default;
  // the entity-scoped "smart match" mode is now opt-in via the toggle.
  const [searchAllBanks, setSearchAllBanks] = useState(true);
  // iter559 — "Hide bank fees & VAT" toggle, default ON. Without this,
  // the picker is flooded by thousands of small VAT/POS-fee lines that
  // dwarf the real supplier-payment lines (most banks emit one VAT
  // tax line per POS transaction). User opens "Add Expense → Pick from
  // Bank" hoping to find a supplier payment but only sees VAT 0.30,
  // VAT 1.43, etc. Toggle is persisted to localStorage.
  const HIDE_FEES_LS_KEY = 'smarto.pickFromBank.hideFees';
  const [hideFees, setHideFees] = useState(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(HIDE_FEES_LS_KEY) : null;
      // Default true (hide fees) — opt-in to see them.
      return raw === null ? true : raw === '1';
    } catch { return true; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(HIDE_FEES_LS_KEY, hideFees ? '1' : '0'); } catch { /* quota / SSR */ }
  }, [hideFees]);
  const [info, setInfo] = useState({ total: 0, truncated: false, mode: '' });
  // iter448 — group expansion state + per-row selection / branch map.
  // iter450 — Persist group expansion state across dialog sessions
  // (localStorage). User's workflow "expand POS Sale Settlement + SPAN
  // Fees, ignore the rest, repeat every month" is now a single-click
  // affair on the next open. Reset is one localStorage clear away.
  const EXPAND_LS_KEY = 'smarto.pickFromBank.expandedGroups';
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(EXPAND_LS_KEY) : null;
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(EXPAND_LS_KEY, JSON.stringify(expandedGroups)); } catch { /* quota / SSR */ }
  }, [expandedGroups]);
  const [picked, setPicked] = useState({});                // rowId → bool
  const [rowBranch, setRowBranch] = useState({});           // rowId → branch_id

  // iter451 — Named "pinned views" persist a snapshot of the dialog's
  // filter state ({name, search, days, direction, expandedGroups}) to
  // localStorage so the user can switch between, say, "Monthly POS
  // reconcile" and "Quarterly Payroll review" in one click.
  // iter452 — Also syncs to the server-side `user_preferences`
  // collection so views travel between browsers / machines.
  const PRESETS_LS_KEY = 'smarto.pickFromBank.presets';
  const PRESETS_SERVER_KEY = 'bulk-pick-presets';
  const [presets, setPresets] = useState(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(PRESETS_LS_KEY) : null;
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  });
  const [serverSynced, setServerSynced] = useState(false);
  // Suppress the autosave that would otherwise fire right after the
  // server pulls in the cloud copy (avoids a no-op PUT round-trip).
  const skipNextPut = useRef(false);
  useEffect(() => {
    try { window.localStorage.setItem(PRESETS_LS_KEY, JSON.stringify(presets)); } catch { /* quota / SSR */ }
    if (skipNextPut.current) { skipNextPut.current = false; return; }
    // Debounced PUT — keep server in sync after every change.
    const handle = setTimeout(async () => {
      try {
        await api.put(`/user-preferences/${PRESETS_SERVER_KEY}`, { value: presets });
        setServerSynced(true);
      } catch { /* offline — localStorage still works */ }
    }, 300);
    return () => clearTimeout(handle);
  }, [presets]);
  // Pull the server copy on dialog open and merge — server wins on
  // collision (more recent edit on another device).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/user-preferences/${PRESETS_SERVER_KEY}`);
        if (cancelled) return;
        const server = Array.isArray(r.data?.value) ? r.data.value : null;
        if (server) {
          skipNextPut.current = true;
          setPresets(server);
          setServerSynced(true);
        }
      } catch { /* offline — keep localStorage copy */ }
    })();
    return () => { cancelled = true; };
  }, [open]);
  const [activePreset, setActivePreset] = useState('');

  const useEntityScope = !!entityType && !!entityId && !searchAllBanks;
  const multiMode = typeof onPickMulti === 'function';

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      if (useEntityScope) {
        const r = await api.get('/banking/statement-txn/match-payee', {
          params: { payee_type: entityType, payee_id: entityId, days, limit: 50 },
        });
        const raw = r.data?.rows || [];
        const needle = search.trim().toLowerCase();
        const filtered = needle ? raw.filter(x => (x.description || '').toLowerCase().includes(needle)) : raw;
        setRows(filtered);
        setInfo({ total: filtered.length, truncated: false, mode: `Matched against ${entityName || entityType}` });
      } else {
        // iter449 — when the picker is in multi/bulk mode, fetch a much
        // larger window so the user can actually see every line of the
        // big repeating groups (POS/SARIE/MASTER fees) without silent
        // truncation. Single-pick keeps 50 — that mode is keyboard-paced
        // through one row at a time.
        const params = { direction, days, limit: multiMode ? 2000 : 50 };
        if (search.trim()) params.name_contains = search.trim();
        if (bankFilter) params.bank_account_id = bankFilter;
        if (amountHint && Number(amountHint) > 0) {
          const h = Number(amountHint);
          params.amount_min = Math.max(0, h * 0.9);
          params.amount_max = h * 1.1;
        }
        // iter553/iter559 — Hide bank fees, SARIE charges, VAT lines, etc.
        // unless the user toggles them on. Previously this was gated to
        // entityType payments only, but the same flood-of-VAT problem
        // happens on Expense / Bill pickers (no entity yet). Now the
        // visible toggle drives it everywhere.
        if (hideFees) {
          params.exclude_fees = true;
        }
        const r = await api.get('/banking/statement-txn/unlinked', { params });
        setRows(r.data?.rows || []);
        const bankLabel = bankFilter
          ? (bankAccounts.find(b => b.id === bankFilter)?.bank_name
              || bankAccounts.find(b => b.id === bankFilter)?.name
              || 'selected bank')
          : 'all banks';
        setInfo({
          total: r.data?.total || 0,
          truncated: r.data?.truncated || false,
          mode: search.trim()
            ? `Searching ${bankLabel} for "${search.trim()}"`
            : bankFilter
              ? `Showing unlinked lines for ${bankLabel}`
              : 'All unlinked bank lines',
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load bank txns');
    } finally {
      setLoading(false);
    }
  }, [useEntityScope, entityType, entityId, entityName, search, days, amountHint, direction, multiMode, bankFilter, bankAccounts, hideFees]);

  // iter462 — Lazy-fetch the tenant's bank-accounts once per open so the
  // filter dropdown is populated. We dedupe by bank_name (one entry per
  // distinct bank) so the UX matches the user's mental model of "pick a
  // bank" rather than "pick an account number".
  useEffect(() => {
    if (!open || bankAccounts.length) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/bank-accounts');
        if (!cancelled) setBankAccounts(r.data || []);
      } catch { /* silently no-op; the picker still works without the filter */ }
    })();
    return () => { cancelled = true; };
  }, [open, bankAccounts.length]);

  useEffect(() => {
    if (open) fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, useEntityScope, bankFilter, hideFees]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { fetchRows(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, days]);

  // Reset multi-pick selection when dialog closes — but KEEP the
  // expanded-groups state so reopening lands on the same view (iter450).
  useEffect(() => {
    if (!open) { setPicked({}); setRowBranch({}); }
  }, [open]);

  // iter451 — Preset handlers.
  const pinCurrentView = () => {
    const name = (window.prompt('Name this view (e.g. "Monthly POS reconcile"):') || '').trim();
    if (!name) return;
    const snapshot = {
      name,
      search,
      days,
      direction,
      expandedGroups: { ...expandedGroups },
    };
    setPresets(p => {
      const existing = p.findIndex(x => x.name.toLowerCase() === name.toLowerCase());
      if (existing >= 0) {
        const next = [...p];
        next[existing] = snapshot;
        return next;
      }
      return [...p, snapshot];
    });
    setActivePreset(name);
    toast.success(`Saved view "${name}"`);
  };
  const applyPreset = (preset) => {
    if (!preset) return;
    setSearch(preset.search || '');
    setDays(preset.days || 180);
    setExpandedGroups(preset.expandedGroups || {});
    setActivePreset(preset.name);
  };
  const removePreset = (name) => {
    setPresets(p => p.filter(x => x.name !== name));
    if (activePreset === name) setActivePreset('');
    toast.success(`Removed view "${name}"`);
  };

  // iter452 — Export / Import preset JSON.
  const exportPresets = () => {
    try {
      const blob = new Blob([JSON.stringify(presets, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `smarto-bulk-pick-presets-${localToday()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${presets.length} preset${presets.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(`Export failed: ${e?.message || 'unknown error'}`);
    }
  };
  const importInputRef = useRef(null);
  const onImportFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) throw new Error('JSON root must be an array of presets');
      // Validate each row at the shape level.
      const cleaned = parsed
        .filter(x => x && typeof x.name === 'string' && x.name.trim())
        .map(x => ({
          name: String(x.name).trim().slice(0, 60),
          search: typeof x.search === 'string' ? x.search : '',
          days: Number.isFinite(+x.days) ? +x.days : 180,
          direction: x.direction === 'incoming' || x.direction === 'all' ? x.direction : 'outgoing',
          expandedGroups: (x.expandedGroups && typeof x.expandedGroups === 'object') ? x.expandedGroups : {},
        }));
      if (!cleaned.length) throw new Error('No valid presets in file');
      // Merge — imports overwrite same-named entries.
      setPresets(prev => {
        const byName = new Map(prev.map(p => [p.name.toLowerCase(), p]));
        for (const p of cleaned) byName.set(p.name.toLowerCase(), p);
        return Array.from(byName.values());
      });
      toast.success(`Imported ${cleaned.length} preset${cleaned.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(`Import failed: ${e?.message || 'unknown error'}`);
    }
  };

  const rowId = (r, i) => `${r.statement_id}-${r.txn_index}-${i}`;

  // iter448 — group rows by `groupKeyFor`. iter462 — optionally also by month.
  const groups = useMemo(() => {
    const m = new Map();
    rows.forEach((r, i) => {
      const k = groupKeyFor(r, groupByMonth);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push({ row: r, i });
    });
    // Sort: largest group first (highest count), then by total absolute amount.
    return Array.from(m.entries())
      .map(([key, items]) => ({
        key,
        items,
        count: items.length,
        total: items.reduce((s, x) => s + (x.row.amount || 0), 0),
        decoded_label: items[0]?.row?.decoded_label || '',
        bank_name: items[0]?.row?.bank_name || '',
      }))
      .sort((a, b) => b.count - a.count || b.total - a.total);
  }, [rows, groupByMonth]);

  const selectedTxns = useMemo(() => {
    const out = [];
    for (const g of groups) {
      for (const { row, i } of g.items) {
        const id = rowId(row, i);
        if (picked[id]) out.push({ txn: row, branch_id: rowBranch[id] || null });
      }
    }
    return out;
  }, [groups, picked, rowBranch]);

  const selectedTotal = selectedTxns.reduce((s, x) => s + (x.txn.amount || 0), 0);

  const toggleAllInGroup = (group, on) => {
    setPicked(prev => {
      const next = { ...prev };
      for (const { row, i } of group.items) {
        const id = rowId(row, i);
        if (on) next[id] = true; else delete next[id];
      }
      return next;
    });
  };

  // iter461 — Toggle: use the LATEST date among selected rows for ALL
  // created records. Useful for "supplier transfer + bank fee + VAT"
  // groups that span two consecutive statement days but logically belong
  // to one settlement day.
  const [useLatestDate, setUseLatestDate] = useState(false);

  const submitMulti = () => {
    if (!selectedTxns.length) {
      toast.error('Tick at least one bank line');
      return;
    }
    // Pass the date strategy as a 2nd arg so consumers can forward it to
    // the /bulk-from-stmt-txns endpoint without a separate prop chain.
    onPickMulti?.(selectedTxns, { dateStrategy: useLatestDate ? 'latest' : 'per_row' });
    onClose?.(true);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-3xl max-h-[88dvh] overflow-hidden flex flex-col p-0"
        data-testid="pick-from-bank-dialog">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Banknote size={18} className="text-info" /> Pick from Bank Statement
            {multiMode && (
              <Badge className="text-[10px] bg-violet-100 text-violet-700 border-violet-200">
                <Layers size={10} className="mr-0.5" /> Bulk pick
              </Badge>
            )}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            {useEntityScope
              ? `Showing unlinked ${direction} bank lines that match ${entityName || entityType}'s name or account number.`
              : 'Showing all unlinked bank lines. Type in the search box to narrow down.'}
            {multiMode
              ? ' Similar lines are grouped — expand a group, tick the lines you want, then click "Add N selected".'
              : ' Click a row to attach it.'}
          </p>
        </DialogHeader>

        {/* Filter bar */}
        <div className="px-6 pb-2 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search description (e.g. supplier name, invoice no.)"
                className="pl-8 h-9"
                data-testid="pick-bank-search" />
            </div>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}
              className="h-9 px-2 text-sm border rounded-md bg-background"
              data-testid="pick-bank-days">
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 6 months</option>
              <option value={365}>Last 12 months</option>
              <option value={730}>Last 24 months</option>
            </select>
            {/* iter462 — Bank-account filter */}
            {bankAccounts.length > 1 && !useEntityScope && (
              <select value={bankFilter} onChange={(e) => setBankFilter(e.target.value)}
                className="h-9 px-2 text-sm border rounded-md bg-background"
                data-testid="pick-bank-account-filter">
                <option value="">All banks</option>
                {bankAccounts.map(ba => (
                  <option key={ba.id} value={ba.id}>
                    {ba.bank_name || ba.name || 'Bank'}
                    {ba.account_number ? ` · ${ba.account_number.slice(-4)}` : ''}
                  </option>
                ))}
              </select>
            )}
            <Button variant="outline" size="sm" onClick={fetchRows} disabled={loading}
              className="h-9" data-testid="pick-bank-refresh">
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
          {/* iter462 — Group-by-month toggle (only meaningful in bulk-pick) */}
          {multiMode && (
            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={groupByMonth}
                onChange={(e) => setGroupByMonth(e.target.checked)}
                className="h-3 w-3"
                data-testid="pick-bank-group-month-toggle" />
              <span>Group by month — collapses repeated fee codes (e.g. SPAN TRM XYZ) into one row per calendar month</span>
            </label>
          )}
          {entityType && entityId && (
            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={!searchAllBanks}
                onChange={(e) => setSearchAllBanks(!e.target.checked)}
                className="h-3 w-3"
                data-testid="pick-bank-search-all-toggle" />
              <span>Smart-match only this {entityName || 'entity'}&apos;s banks (uncheck to see every bank)</span>
            </label>
          )}
          {/* iter559 — Hide bank fees / VAT toggle (default ON). Without
              this, the picker is buried under thousands of small VAT
              lines and the user can't find real supplier payments. */}
          <label className="flex items-center gap-1.5 text-[11px] cursor-pointer" data-testid="pick-bank-hide-fees-label">
            <input
              type="checkbox"
              checked={hideFees}
              onChange={(e) => setHideFees(e.target.checked)}
              className="h-3 w-3"
              data-testid="pick-bank-hide-fees-toggle"
            />
            <span className={hideFees ? 'text-foreground font-medium' : 'text-muted-foreground'}>
              Hide bank fees, VAT & SARIE charges
              <span className="ml-1 text-muted-foreground font-normal">— uncheck to see every line, including fee entries</span>
            </span>
          </label>
          <div className="text-[10px] text-muted-foreground flex items-center justify-between">
            <span>{info.mode}</span>
            <div className="flex items-center gap-3">
              <span>
                {multiMode
                  ? `${groups.length} group${groups.length === 1 ? '' : 's'} · ${info.total} line${info.total === 1 ? '' : 's'}`
                  : `${info.total} match${info.total === 1 ? '' : 'es'}`}
                {info.truncated ? ' (truncated — refine the search)' : ''}
              </span>
              {multiMode && (
                <button
                  type="button"
                  onClick={pinCurrentView}
                  className="text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900"
                  title="Save the current search + days + expanded groups as a named preset"
                  data-testid="pick-bank-pin-view">
                  <Pin size={10} /> Pin this view
                </button>
              )}
              {multiMode && Object.keys(expandedGroups || {}).length > 0 && (
                <button
                  type="button"
                  onClick={() => setExpandedGroups({})}
                  className="text-[10px] underline text-muted-foreground hover:text-foreground"
                  title="Collapse every group and forget the remembered state"
                  data-testid="pick-bank-reset-expansion">
                  Reset expanded
                </button>
              )}
            </div>
          </div>
          {/* iter451 — Saved presets as clickable chips. */}
          {multiMode && (presets.length > 0 || serverSynced) && (
            <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid="pick-bank-presets">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                Saved views:
                {serverSynced && (
                  <Cloud size={11} className="text-emerald-600"
                    title="Synced to your account — these views travel between browsers"
                    data-testid="pick-bank-preset-cloud" />
                )}
              </span>
              {presets.map((p) => {
                const isActive = activePreset === p.name;
                return (
                  <span key={p.name}
                    className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full border text-[11px] transition-colors ${
                      isActive
                        ? 'bg-amber-100 text-amber-900 border-amber-400'
                        : 'bg-white text-foreground border-amber-300 hover:bg-amber-50'
                    }`}
                    data-testid={`pick-bank-preset-${p.name.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30)}`}>
                    <button type="button" onClick={() => applyPreset(p)}
                      className="font-medium" title={`Apply "${p.name}"`}>
                      <Pin size={9} className="inline mr-0.5" />{p.name}
                    </button>
                    <button type="button"
                      onClick={() => removePreset(p.name)}
                      className="h-4 w-4 rounded-full text-muted-foreground hover:text-error hover:bg-error/10 flex items-center justify-center"
                      title="Delete this preset"
                      data-testid={`pick-bank-preset-delete-${p.name.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30)}`}>
                      <X size={10} />
                    </button>
                  </span>
                );
              })}
              {/* iter452 — Export / Import menu */}
              <div className="ml-auto flex items-center gap-2">
                {presets.length > 0 && (
                  <button type="button" onClick={exportPresets}
                    className="text-[10px] underline text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
                    title="Download these views as a JSON file"
                    data-testid="pick-bank-presets-export">
                    <FileDown size={10} /> Export
                  </button>
                )}
                <label className="text-[10px] underline text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5 cursor-pointer"
                  title="Load views from a JSON file (merges with existing)"
                  data-testid="pick-bank-presets-import-label">
                  <FileUp size={10} /> Import
                  <input ref={importInputRef} type="file" accept="application/json,.json"
                    onChange={(e) => { onImportFile(e.target.files?.[0]); e.target.value = ''; }}
                    className="hidden" data-testid="pick-bank-presets-import-input" />
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Rows */}
        <div className="flex-1 overflow-y-auto border-t border-b">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground">
              <Loader2 className="animate-spin inline mb-2" size={20} />
              <div className="text-xs">Loading bank lines…</div>
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground text-sm">
              No unlinked bank lines found.
              {useEntityScope && (
                <div className="mt-2 text-xs">
                  Tick &quot;Search all banks&quot; above to widen the search.
                </div>
              )}
            </div>
          ) : multiMode ? (
            <div className="divide-y" data-testid="pick-bank-groups">
              {groups.map((g) => {
                const groupAllPicked = g.items.every(({ row, i }) => picked[rowId(row, i)]);
                const groupSomePicked = g.items.some(({ row, i }) => picked[rowId(row, i)]);
                const expanded = !!expandedGroups[g.key] || groups.length === 1;
                return (
                  <div key={g.key} data-testid={`pick-bank-group-${g.key.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 40)}`}>
                    {/* Group header */}
                    <div className="w-full px-4 py-2 flex items-center gap-2 hover:bg-muted/30">
                      <input
                        type="checkbox"
                        checked={groupAllPicked}
                        ref={el => { if (el) el.indeterminate = groupSomePicked && !groupAllPicked; }}
                        onChange={(e) => toggleAllInGroup(g, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        className="accent-violet-600"
                        data-testid={`pick-bank-group-check-${g.key.slice(0, 30)}`}
                      />
                      <button type="button"
                        onClick={() => setExpandedGroups(s => ({ ...s, [g.key]: !s[g.key] }))}
                        className="flex-1 flex items-center gap-2 text-left text-sm min-w-0">
                        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <span className="font-medium truncate" title={g.key}>{g.key}</span>
                        {g.decoded_label && (
                          <Badge className="text-[10px] bg-sky-100 text-sky-800 border-sky-200">{g.decoded_label}</Badge>
                        )}
                        <Badge variant="outline" className="text-[10px]">{g.count}× lines</Badge>
                        {g.bank_name && (
                          <Badge variant="outline" className="text-[10px]"><Building2 size={9} className="mr-0.5" />{g.bank_name}</Badge>
                        )}
                      </button>
                      {/* iter449 — Quick-categorise group: one click books
                          all rows in this group as expenses with the
                          decoded label as the auto-category (or whatever
                          mapping the parent decides). */}
                      {typeof onQuickGroup === 'function' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            onQuickGroup({
                              key: g.key,
                              decoded_label: g.decoded_label,
                              bank_name: g.bank_name,
                              total: g.total,
                              txns: g.items.map(x => x.row),
                            });
                          }}
                          className="h-6 px-2 text-[10px] bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300"
                          title="Auto-categorise every line in this group as expenses"
                          data-testid={`pick-bank-quick-group-${g.key.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30)}`}>
                          <Zap size={11} className="mr-0.5" /> Quick-categorise
                        </Button>
                      )}
                      <span className={`text-sm font-bold ${g.total >= 0 ? 'text-success' : 'text-error'}`}>
                        {cur()} {g.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    {/* Group body */}
                    {expanded && (
                      <div className="bg-muted/20 divide-y divide-border/60">
                        {g.items.map(({ row: r, i }) => {
                          const id = rowId(r, i);
                          return (
                            <label
                              key={id}
                              className="flex items-center gap-2 pl-10 pr-4 py-1.5 text-xs hover:bg-muted/40 cursor-pointer"
                              data-testid={`pick-bank-multi-row-${i}`}>
                              <input
                                type="checkbox"
                                checked={!!picked[id]}
                                onChange={(e) => {
                                  setPicked(p => ({ ...p, [id]: e.target.checked }));
                                  if (!e.target.checked) setRowBranch(m => { const n = { ...m }; delete n[id]; return n; });
                                }}
                                className="accent-violet-600"
                              />
                              <span className="font-mono text-[10px] text-muted-foreground min-w-[80px]">{r.date}</span>
                              <span className="flex-1 truncate" title={r.description}>{r.description}</span>
                              {branches.length > 0 && picked[id] && (
                                <select
                                  value={rowBranch[id] || ''}
                                  onChange={(e) => setRowBranch(m => ({ ...m, [id]: e.target.value }))}
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-6 text-[10px] border rounded px-1 bg-white"
                                  data-testid={`pick-bank-row-branch-${i}`}>
                                  <option value="">Default branch</option>
                                  {branches.map(b => (
                                    <option key={b.id} value={b.id}>{b.name || b.code || b.id.slice(0, 6)}</option>
                                  ))}
                                </select>
                              )}
                              <span className={`font-bold whitespace-nowrap ${r.direction === 'incoming' ? 'text-success' : 'text-error'}`}>
                                {r.direction === 'incoming' ? '+' : '−'} {cur()} {r.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="divide-y" data-testid="pick-bank-rows">
              {rows.map((r, i) => {
                // iter553 — Defensive: even though backend now filters fees
                // when entityType is supplier/customer/employee/partner,
                // legacy/edge-case rows (un-decoded fees) might still slip
                // through. Tag them visually so the user doesn't pick by
                // mistake.
                const _feeLabels = new Set([
                  'pos_fees', 'pos_fee_coll', 'pos_monthly_fee',
                  'master_card_fees', 'visa_fees', 'span_fees', 'mada_fees',
                  'vat_fees', 'sarie_charges', 'bank_fees', 'sadad_bills',
                ]);
                const isFee = _feeLabels.has((r.decoded_label || '').toLowerCase());
                const isSupplierLike = ['supplier', 'customer', 'employee', 'partner'].includes(entityType);
                return (
                <button
                  type="button"
                  key={`${r.statement_id}-${r.txn_index}-${i}`}
                  onClick={() => {
                    if (isFee && isSupplierLike) {
                      if (!window.confirm(`⚠️  This row looks like a bank FEE (${r.decoded_label || 'fee/charge'}).\n\nIf you pick it, the form amount will become ${cur()} ${Number(r.amount || 0).toFixed(2)} — which is the FEE, not the actual payment.\n\nAre you sure you want to attach this line?`)) {
                        return;
                      }
                    }
                    onPick?.(r);
                    onClose?.(true);
                  }}
                  className={`w-full text-left px-6 py-3 hover:bg-info/5 transition-colors flex items-center gap-3 ${
                    isFee && isSupplierLike ? 'bg-amber-50/40' : ''
                  }`}
                  data-testid={`pick-bank-row-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-xs text-muted-foreground font-mono">{r.date}</span>
                      <Badge variant="outline" className="text-[10px] inline-flex items-center gap-0.5">
                        <Building2 size={9} /> {r.bank_name || '—'}
                      </Badge>
                      {r.decoded_label && (
                        <Badge className={`text-[10px] ${isFee ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-sky-100 text-sky-800 border-sky-200'}`}
                          title={`Auto-decoded from "${(r.description || '').slice(0, 60)}…"`}
                          data-testid={`pick-bank-decoded-${i}`}>
                          {r.decoded_label}
                        </Badge>
                      )}
                      {isFee && isSupplierLike && (
                        <Badge className="text-[10px] bg-rose-100 text-rose-700 border-rose-300 font-semibold" title="Bank fee — do NOT attach as a supplier payment">
                          ⚠ Bank Fee
                        </Badge>
                      )}
                      {r.match_via && (
                        <Badge className="text-[10px] bg-violet-100 text-violet-700 border-violet-200">
                          via {r.match_via}
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm font-medium truncate" title={r.description}>{r.description}</div>
                    {r.counterparty_name && (
                      <div className="text-[10px] text-muted-foreground truncate">→ {r.counterparty_name}</div>
                    )}
                  </div>
                  <div className={`text-right font-bold text-sm whitespace-nowrap ${r.direction === 'incoming' ? 'text-success' : 'text-error'}`}>
                    {r.direction === 'incoming' ? '+' : '−'} {cur()} {r.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </button>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-3 gap-2 sm:gap-2">
          {multiMode && (
            <div className="flex-1 text-xs text-muted-foreground">
              {selectedTxns.length > 0 ? (
                <span>
                  <span className="font-semibold text-foreground">{selectedTxns.length}</span> selected
                  {' · '}
                  <span className="font-semibold text-foreground">{cur()} {selectedTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </span>
              ) : (
                <span>Tick any rows you want to add</span>
              )}
            </div>
          )}
          {multiMode && selectedTxns.length > 1 && (
            <label
              className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none mr-2"
              title="When ticked, every created record gets the most recent date among the selected rows — useful for grouping a supplier transfer + bank fee + VAT into a single settlement day."
            >
              <input
                type="checkbox"
                checked={useLatestDate}
                onChange={(e) => setUseLatestDate(e.target.checked)}
                className="h-3 w-3"
                data-testid="pick-bank-latest-date-toggle"
              />
              <span>Use latest date for all</span>
            </label>
          )}
          <Button variant="outline" onClick={() => onClose?.()} data-testid="pick-bank-cancel">
            Cancel
          </Button>
          {multiMode && (
            <Button
              onClick={submitMulti}
              disabled={!selectedTxns.length}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="pick-bank-add-selected">
              Add {selectedTxns.length || ''} selected
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
